import { Injectable, inject } from '@angular/core';
import { ContextService, FeatureService } from '@maximizer/core/shared/data-access';
import { DocumentTimelineEvent } from '@maximizer/core/shared/domain';
import { OpenEntityArgs, ShowPreviewDialogArgs } from '@maximizer/core/shared/ui/config';
import { TimelineService } from '@maximizer/core/timeline/data-access';
import { catchError, map, Observable, of } from 'rxjs';

// Core's document viewer component, registered under this tag by the AI app itself
// (apps/ai/src/main.ts): the dialog, the viewer and its pdf.js runtime are served by the AI
// deployment, so opening a PDF never depends on what the core deployment contains.
export const AI_PREVIEW_DIALOG_TAG = 'maximizer-ai-preview-dialog';

// The timeline's flag for its viewer: while it is off, documents download as before.
export const DOCUMENT_PREVIEW_FEATURE_FLAG = 'core-timeline-document-preview-shown';

const isPdf = (fileName: string): boolean => fileName.toLowerCase().endsWith('.pdf');

@Injectable()
export class IQBoostDocumentPreviewService {
  private readonly context = inject(ContextService);
  private readonly feature = inject(FeatureService);
  private readonly timeline = inject(TimelineService);

  // Emits once: false leaves the citation to the host, as does a document that cannot be read.
  open(args: OpenEntityArgs): Observable<boolean> {
    const { key, type } = args.entity;
    const enabled = this.feature.isFeatureOn(DOCUMENT_PREVIEW_FEATURE_FLAG, false);
    if (type !== 'document' || !key || !enabled) {
      return of(false);
    }

    // The citation carries only the key; the viewer needs the file name its timeline event has.
    return this.timeline.forEvent(key).pipe(
      map((event) => {
        const fileName = event?.type === 'document' ? (event as DocumentTimelineEvent).file : '';
        if (!fileName || !isPdf(fileName)) {
          return false;
        }
        this.context.showComponentDialog(
          AI_PREVIEW_DIALOG_TAG,
          new ShowPreviewDialogArgs(AI_PREVIEW_DIALOG_TAG, key, fileName, true),
        );
        return true;
      }),
      catchError(() => of(false)),
    );
  }
}
